import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, HStack, IconButton, VStack } from '@chakra-ui/react';
import { FaHighlighter, FaTrash, FaUndo } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { API_BASE } from '../api';

const HIGHLIGHT_MODE = 'token-overlay-v1';
const HIGHLIGHT_SCOPE_ATTR = 'data-highlight-scope';
const HIGHLIGHT_SCOPE_VALUE = 'content';
const TOKEN_CLASS = 'igm-token';
const OCR_TOKEN_CLASS = 'igm-ocr-word';
const PDF_TOKEN_CLASS = 'igm-pdf-token';
const TOKEN_ATTR = 'data-token-id';
const PDF_ANNOTATION_ROOT_ATTR = 'data-pdf-annotation-root';
const TOUCH_SELECT_UPDATE_MS = 16;
const TOUCH_SELECT_TAP_MOVE_LIMIT_PX = 8;
const LINE_Y_TOLERANCE = 6;
const LINE_PADDING = 10;

const WORD_REGEX = /[0-9A-Za-zÀ-ÖØ-öø-ÿ]+/g;

const getUserId = (user) => {
  const parsed = Number.parseInt(user?.id_pessoa || user?.id || 0, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const createHighlightId = () => `h_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const isControlTarget = (element) => {
  if (!element || typeof element.closest !== 'function') {
    return false;
  }
  return Boolean(element.closest('[data-highlighter-control="true"]'));
};

const isInHighlightScope = (element) => {
  if (!element || typeof element.closest !== 'function') {
    return false;
  }
  return Boolean(element.closest(`[${HIGHLIGHT_SCOPE_ATTR}="${HIGHLIGHT_SCOPE_VALUE}"]`));
};

const isPdfAnnotationTarget = (element) => {
  if (!element || typeof element.closest !== 'function') {
    return false;
  }
  return Boolean(element.closest(`[${PDF_ANNOTATION_ROOT_ATTR}="true"]`));
};

const getRouteRoot = () => document.getElementById('igm-route-root') || document.body;

const getScopeRoot = () => {
  const routeRoot = getRouteRoot();
  const scoped = routeRoot.querySelector(`[${HIGHLIGHT_SCOPE_ATTR}="${HIGHLIGHT_SCOPE_VALUE}"]`);
  return scoped || routeRoot;
};

const normalizeStoredPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  if (payload.mode !== HIGHLIGHT_MODE || !Array.isArray(payload.highlights)) {
    return [];
  }
  return payload.highlights.filter(
    (item) => item && Number.isFinite(item.startToken) && Number.isFinite(item.endToken)
  );
};

const getTokenElements = (root) =>
  Array.from(root.querySelectorAll(`.${TOKEN_CLASS}, .${OCR_TOKEN_CLASS}, .${PDF_TOKEN_CLASS}`));

const shouldSkipTextNode = (node) => {
  if (!node || node.nodeType !== Node.TEXT_NODE) return true;
  if (!node.textContent || !node.textContent.trim()) return true;
  const parent = node.parentElement;
  if (!parent) return true;
  if (parent.closest('[data-highlighter-control="true"]')) return true;
  if (parent.closest(`[${PDF_ANNOTATION_ROOT_ATTR}="true"]`)) return true;
  if (parent.closest(`.${OCR_TOKEN_CLASS}`)) return true;
  if (parent.closest('.react-pdf__Page__textContent')) return true;
  const tag = parent.tagName?.toLowerCase?.() || '';
  if (['script', 'style', 'noscript', 'textarea', 'input', 'select', 'option', 'button'].includes(tag)) {
    return true;
  }
  return false;
};

const wrapTextNodeTokens = (node) => {
  const text = node.textContent || '';
  WORD_REGEX.lastIndex = 0;
  let lastIndex = 0;
  const frag = document.createDocumentFragment();
  let match = WORD_REGEX.exec(text);
  let hadToken = false;
  while (match) {
    const start = match.index;
    const end = start + match[0].length;
    if (start > lastIndex) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex, start)));
    }
    const span = document.createElement('span');
    span.className = TOKEN_CLASS;
    span.textContent = match[0];
    frag.appendChild(span);
    hadToken = true;
    lastIndex = end;
    match = WORD_REGEX.exec(text);
  }
  if (!hadToken) {
    return false;
  }
  if (lastIndex < text.length) {
    frag.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
  node.parentNode.replaceChild(frag, node);
  return true;
};

const buildTokens = (root) => {
  if (!root) return [];

  const pdfTextLayer = root.querySelector('.react-pdf__Page__textContent');
  if (pdfTextLayer) {
    const pdfSpans = Array.from(pdfTextLayer.querySelectorAll('span'));
    pdfSpans.forEach((span) => {
      if (!span.textContent || !span.textContent.trim()) return;
      span.classList.add(PDF_TOKEN_CLASS);
    });
  }

  const existing = root.querySelector(`.${TOKEN_CLASS}`);
  if (!existing) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => (shouldSkipTextNode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT)
    });

    let current = walker.nextNode();
    const toProcess = [];
    while (current) {
      toProcess.push(current);
      current = walker.nextNode();
    }

    toProcess.forEach((node) => {
      wrapTextNodeTokens(node);
    });
  }

  const tokens = getTokenElements(root).map((el, idx) => {
    el.setAttribute(TOKEN_ATTR, String(idx));
    return { id: idx, el, text: el.textContent || '' };
  });

  return tokens;
};

const getTokenIndexFromElement = (el) => {
  if (!el) return null;
  const tokenEl = el.closest?.(`.${TOKEN_CLASS}, .${OCR_TOKEN_CLASS}`) || null;
  if (!tokenEl) return null;
  const raw = tokenEl.getAttribute(TOKEN_ATTR);
  const idx = Number.parseInt(raw || '', 10);
  return Number.isFinite(idx) ? idx : null;
};

const getNearestTokenIndexByPoint = (tokens, clientX, clientY) => {
  if (!tokens.length) return null;
  let bestIdx = null;
  let bestDist = Number.POSITIVE_INFINITY;
  const viewTop = -60;
  const viewBottom = window.innerHeight + 60;

  for (let i = 0; i < tokens.length; i += 1) {
    const rects = tokens[i].el.getClientRects();
    for (let r = 0; r < rects.length; r += 1) {
      const rect = rects[r];
      if (!rect || rect.width <= 0 || rect.height <= 0) continue;
      if (rect.bottom < viewTop || rect.top > viewBottom) continue;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = clientX - cx;
      const dy = clientY - cy;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
  }
  return bestIdx;
};

const buildLineMap = (tokens) => {
  const lines = [];
  tokens.forEach((token, tokenIdx) => {
    const rects = token.el.getClientRects();
    for (let r = 0; r < rects.length; r += 1) {
      const rect = rects[r];
      if (!rect || rect.width <= 0 || rect.height <= 0) continue;
      const centerY = rect.top + rect.height / 2;
      let line = null;
      for (let i = 0; i < lines.length; i += 1) {
        if (Math.abs(lines[i].centerY - centerY) <= LINE_Y_TOLERANCE) {
          line = lines[i];
          break;
        }
      }
      if (!line) {
        line = {
          top: rect.top,
          bottom: rect.bottom,
          centerY,
          items: []
        };
        lines.push(line);
      } else {
        line.top = Math.min(line.top, rect.top);
        line.bottom = Math.max(line.bottom, rect.bottom);
        line.centerY = (line.top + line.bottom) / 2;
      }
      line.items.push({ tokenIdx, rect, centerX: rect.left + rect.width / 2 });
    }
  });

  lines.forEach((line) => {
    line.items.sort((a, b) => a.rect.left - b.rect.left);
  });
  lines.sort((a, b) => a.top - b.top);
  return lines;
};

const getTokenIndexFromLineMap = (lineMap, clientX, clientY) => {
  if (!lineMap.length) return null;
  let bestLine = null;
  let bestLineDist = Number.POSITIVE_INFINITY;

  for (let i = 0; i < lineMap.length; i += 1) {
    const line = lineMap[i];
    const within =
      clientY >= line.top - LINE_PADDING && clientY <= line.bottom + LINE_PADDING;
    const dist = within ? 0 : Math.abs(clientY - line.centerY);
    if (dist < bestLineDist) {
      bestLineDist = dist;
      bestLine = line;
    }
  }

  if (!bestLine || !bestLine.items.length) return null;

  // Prefer item whose rect contains the point
  for (let i = 0; i < bestLine.items.length; i += 1) {
    const rect = bestLine.items[i].rect;
    if (
      clientX >= rect.left - 6 &&
      clientX <= rect.right + 6 &&
      clientY >= rect.top - 6 &&
      clientY <= rect.bottom + 6
    ) {
      return bestLine.items[i].tokenIdx;
    }
  }

  // Otherwise pick nearest x in that line
  let bestItem = null;
  let bestX = Number.POSITIVE_INFINITY;
  for (let i = 0; i < bestLine.items.length; i += 1) {
    const dx = Math.abs(clientX - bestLine.items[i].centerX);
    if (dx < bestX) {
      bestX = dx;
      bestItem = bestLine.items[i];
    }
  }
  return bestItem ? bestItem.tokenIdx : null;
};

const getTokenIndexFromPoint = (tokens, lineMap, clientX, clientY) => {
  const el = document.elementFromPoint(clientX, clientY);
  const directIdx = getTokenIndexFromElement(el);
  if (directIdx != null) return directIdx;
  if (!tokens.length) return null;
  if (lineMap && lineMap.length) {
    const idx = getTokenIndexFromLineMap(lineMap, clientX, clientY);
    if (idx != null) return idx;
  }
  return getNearestTokenIndexByPoint(tokens, clientX, clientY);
};

const normalizeRange = (start, end) => {
  const min = Math.min(start, end);
  const max = Math.max(start, end);
  return { start: min, end: max };
};

const buildHighlightFromRange = (tokens, startIdx, endIdx, existingId) => {
  if (!tokens.length) return null;
  const boundedStart = Math.max(0, Math.min(startIdx, tokens.length - 1));
  const boundedEnd = Math.max(0, Math.min(endIdx, tokens.length - 1));
  const range = normalizeRange(boundedStart, boundedEnd);
  const slice = tokens.slice(range.start, range.end + 1);
  const text = slice.map((t) => t.text).join(' ').slice(0, 400);
  return {
    id: existingId || createHighlightId(),
    startToken: range.start,
    endToken: range.end,
    text
  };
};

const GlobalTextHighlighter = ({ user }) => {
  const { pathname, search } = useLocation();
  const [markerEnabled, setMarkerEnabled] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [isLoadedFromApi, setIsLoadedFromApi] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [tokenVersion, setTokenVersion] = useState(0);
  const [layoutTick, setLayoutTick] = useState(0);
  const [previewRange, setPreviewRange] = useState(null);
  const [editingHighlightId, setEditingHighlightId] = useState(null);

  const highlightsRef = useRef([]);
  const tokensRef = useRef([]);
  const lineMapRef = useRef([]);
  const skipNativeSelectionRef = useRef(false);
  const selectionFinalizeTimerRef = useRef(null);
  const dragStateRef = useRef({ active: false, mode: null });
  const anchorIdxRef = useRef(null);
  const currentIdxRef = useRef(null);
  const touchStartPointRef = useRef(null);
  const touchLastSampleRef = useRef(0);

  const userId = useMemo(() => getUserId(user), [user]);
  const routePathKey = useMemo(() => `${pathname}${search || ''}`, [pathname, search]);
  const canPersist = Boolean(userId) && pathname !== '/login';

  useEffect(() => {
    highlightsRef.current = highlights;
  }, [highlights]);

  useEffect(() => {
    setMarkerEnabled(false);
    setIsPanelOpen(false);
    setPreviewRange(null);
    setEditingHighlightId(null);
  }, [routePathKey]);

  useEffect(() => {
    document.body.classList.toggle('igm-screen-marker-active', markerEnabled);
    return () => {
      document.body.classList.remove('igm-screen-marker-active');
    };
  }, [markerEnabled]);

  useEffect(() => {
    if (!canPersist) {
      highlightsRef.current = [];
      setHighlights([]);
      setIsLoadedFromApi(false);
      setIsDirty(false);
      return;
    }

    let isActive = true;
    setIsLoadedFromApi(false);

    const loadHighlights = async () => {
      try {
        const endpoint = `${API_BASE}/user-highlights?id_pessoa=${userId}&route_path=${encodeURIComponent(routePathKey)}`;
        const response = await fetch(endpoint);
        const payload = await response.json();

        if (!isActive) {
          return;
        }

        const normalized = normalizeStoredPayload(payload?.selectors);
        highlightsRef.current = normalized;
        setHighlights(normalized);
      } catch (error) {
        if (isActive) {
          highlightsRef.current = [];
          setHighlights([]);
        }
      } finally {
        if (isActive) {
          setIsLoadedFromApi(true);
          setIsDirty(false);
        }
      }
    };

    loadHighlights();

    return () => {
      isActive = false;
    };
  }, [canPersist, routePathKey, userId]);

  const rebuildTokens = useCallback(() => {
    const root = getScopeRoot();
    if (!root) return;
    const tokens = buildTokens(root);
    tokensRef.current = tokens;
    lineMapRef.current = buildLineMap(tokens);
    setTokenVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      rebuildTokens();
    }, 120);
    return () => clearTimeout(timer);
  }, [rebuildTokens, routePathKey]);

  useEffect(() => {
    const root = getScopeRoot();
    if (!root) return undefined;
    let pending = null;
    const observer = new MutationObserver(() => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => {
        rebuildTokens();
      }, 80);
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      if (pending) clearTimeout(pending);
      observer.disconnect();
    };
  }, [rebuildTokens, routePathKey]);

  useEffect(() => {
    const onLayout = () => {
      setLayoutTick((t) => t + 1);
      const tokens = tokensRef.current;
      if (tokens.length) {
        lineMapRef.current = buildLineMap(tokens);
      }
    };
    const rafHandler = () => {
      requestAnimationFrame(onLayout);
    };
    window.addEventListener('scroll', rafHandler, { passive: true });
    window.addEventListener('resize', rafHandler);
    return () => {
      window.removeEventListener('scroll', rafHandler);
      window.removeEventListener('resize', rafHandler);
    };
  }, []);

  const updatePreview = useCallback((startIdx, endIdx) => {
    if (startIdx == null || endIdx == null) return;
    const normalized = normalizeRange(startIdx, endIdx);
    setPreviewRange((prev) => {
      if (!prev || prev.start !== normalized.start || prev.end !== normalized.end) {
        return normalized;
      }
      return prev;
    });
  }, []);

  const commitPreview = useCallback((range, existingId) => {
    if (!range) return;
    const tokens = tokensRef.current;
    if (!tokens.length) return;
    const highlight = buildHighlightFromRange(tokens, range.start, range.end, existingId);
    if (!highlight) return;

    setHighlights((prev) => {
      const hasExisting = prev.some((item) => item.id === highlight.id);
      const next = hasExisting
        ? prev.map((item) => (item.id === highlight.id ? highlight : item))
        : [...prev, highlight];
      highlightsRef.current = next;
      return next;
    });
    setEditingHighlightId(highlight.id);
    setIsDirty(true);
  }, []);

  const findHighlightForToken = useCallback((tokenIdx) => {
    if (tokenIdx == null) return null;
    return highlightsRef.current.find(
      (item) => tokenIdx >= item.startToken && tokenIdx <= item.endToken
    );
  }, []);

  useEffect(() => {
    if (!markerEnabled) {
      dragStateRef.current = { active: false, mode: null };
      anchorIdxRef.current = null;
      currentIdxRef.current = null;
      touchStartPointRef.current = null;
      touchLastSampleRef.current = 0;
      setPreviewRange(null);
      return;
    }

    const onPointerStart = (event) => {
      if (!markerEnabled) return;
      const point = event.touches?.[0] || event;
      if (!point) return;
      const target = document.elementFromPoint(point.clientX, point.clientY);
      if (!target || isControlTarget(target) || isPdfAnnotationTarget(target) || !isInHighlightScope(target)) return;

      const tokens = tokensRef.current;
      if (!tokens.length) {
        rebuildTokens();
      }
      const liveTokens = tokensRef.current;
      if (!liveTokens.length) return;

      const idx = getTokenIndexFromPoint(liveTokens, lineMapRef.current, point.clientX, point.clientY);
      if (idx == null) return;

      const existing = findHighlightForToken(idx);
      if (existing) {
        setEditingHighlightId(existing.id);
        updatePreview(existing.startToken, existing.endToken);
      } else {
        setEditingHighlightId(null);
        updatePreview(idx, idx);
      }

      dragStateRef.current = { active: true, mode: 'range' };
      anchorIdxRef.current = existing ? existing.startToken : idx;
      currentIdxRef.current = idx;
      touchStartPointRef.current = { x: point.clientX, y: point.clientY };
      touchLastSampleRef.current = Date.now();
      skipNativeSelectionRef.current = true;
      if (event.cancelable) event.preventDefault();
    };

    const onPointerMove = (event) => {
      if (!dragStateRef.current.active) return;
      const point = event.touches?.[0] || event;
      if (!point) return;

      const now = Date.now();
      if (now - touchLastSampleRef.current < TOUCH_SELECT_UPDATE_MS) return;
      touchLastSampleRef.current = now;

      const tokens = tokensRef.current;
      if (!tokens.length) {
        rebuildTokens();
      }
      const liveTokens = tokensRef.current;
      const idx = getTokenIndexFromPoint(liveTokens, lineMapRef.current, point.clientX, point.clientY);
      if (idx == null) {
        // keep last valid index to avoid dropping selection on gaps
        if (currentIdxRef.current == null) return;
        updatePreview(anchorIdxRef.current, currentIdxRef.current);
        if (event.cancelable) event.preventDefault();
        return;
      }

      currentIdxRef.current = idx;
      if (dragStateRef.current.mode === 'range') {
        updatePreview(anchorIdxRef.current, currentIdxRef.current);
      } else if (dragStateRef.current.mode === 'start' && previewRange) {
        updatePreview(idx, previewRange.end);
      } else if (dragStateRef.current.mode === 'end' && previewRange) {
        updatePreview(previewRange.start, idx);
      }
      if (event.cancelable) event.preventDefault();
    };

    const onPointerEnd = (event) => {
      if (!dragStateRef.current.active) return;
      const point = event.changedTouches?.[0] || event;
      const distance = point && touchStartPointRef.current
        ? Math.hypot(point.clientX - touchStartPointRef.current.x, point.clientY - touchStartPointRef.current.y)
        : 0;

      if (distance <= TOUCH_SELECT_TAP_MOVE_LIMIT_PX && point) {
        const tokens = tokensRef.current;
        if (!tokens.length) {
          rebuildTokens();
        }
        const liveTokens = tokensRef.current;
        const idx = getTokenIndexFromPoint(liveTokens, lineMapRef.current, point.clientX, point.clientY);
        if (idx != null) {
          updatePreview(idx, idx);
        }
      }

      if (previewRange) {
        const existingId = editingHighlightId || null;
        commitPreview(previewRange, existingId);
      }

      dragStateRef.current = { active: false, mode: null };
      anchorIdxRef.current = null;
      currentIdxRef.current = null;
      touchStartPointRef.current = null;
      touchLastSampleRef.current = 0;
      setPreviewRange(null);
      setTimeout(() => {
        skipNativeSelectionRef.current = false;
      }, 0);
      if (event?.cancelable) event.preventDefault();
    };

    const options = { passive: false };
    document.addEventListener('touchstart', onPointerStart, options);
    document.addEventListener('touchmove', onPointerMove, options);
    document.addEventListener('touchend', onPointerEnd, options);
    document.addEventListener('touchcancel', onPointerEnd, options);
    document.addEventListener('mousedown', onPointerStart, options);
    document.addEventListener('mousemove', onPointerMove, options);
    document.addEventListener('mouseup', onPointerEnd, options);

    return () => {
      document.removeEventListener('touchstart', onPointerStart, options);
      document.removeEventListener('touchmove', onPointerMove, options);
      document.removeEventListener('touchend', onPointerEnd, options);
      document.removeEventListener('touchcancel', onPointerEnd, options);
      document.removeEventListener('mousedown', onPointerStart, options);
      document.removeEventListener('mousemove', onPointerMove, options);
      document.removeEventListener('mouseup', onPointerEnd, options);
    };
  }, [commitPreview, editingHighlightId, findHighlightForToken, markerEnabled, previewRange, updatePreview]);

  useEffect(() => {
    if (!canPersist || !isLoadedFromApi || !isDirty) {
      return;
    }

    const timer = setTimeout(async () => {
      try {
        if (highlights.length === 0) {
          await fetch(`${API_BASE}/user-highlights`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_pessoa: userId,
              route_path: routePathKey
            })
          });
        } else {
          await fetch(`${API_BASE}/user-highlights`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_pessoa: userId,
              route_path: routePathKey,
              selectors: {
                mode: HIGHLIGHT_MODE,
                version: 1,
                highlights
              }
            })
          });
        }
        setIsDirty(false);
      } catch (error) {
        // Mantem estado local se a API falhar.
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [canPersist, highlights, isLoadedFromApi, isDirty, routePathKey, userId]);

  const handleClearHighlights = () => {
    highlightsRef.current = [];
    setHighlights([]);
    setIsDirty(true);
  };

  const handleUndoLastHighlight = () => {
    if (highlightsRef.current.length === 0) {
      return;
    }

    setHighlights((previous) => {
      const next = previous.slice(0, -1);
      highlightsRef.current = next;
      return next;
    });
    setIsDirty(true);
  };

  const handleHandleStart = (mode) => (event) => {
    if (!markerEnabled) return;
    if (!previewRange && editingHighlightId) {
      const existing = highlightsRef.current.find((item) => item.id === editingHighlightId);
      if (existing) {
        setPreviewRange({ start: existing.startToken, end: existing.endToken });
      }
    }
    dragStateRef.current = { active: true, mode };
    if (event?.cancelable) event.preventDefault();
  };

  const overlayData = useMemo(() => {
    const tokens = tokensRef.current;
    if (!tokens.length) return { rects: [], previewRects: [], handles: null };

    const rects = [];
    highlights.forEach((hl) => {
      const start = Math.max(0, Math.min(hl.startToken, tokens.length - 1));
      const end = Math.max(0, Math.min(hl.endToken, tokens.length - 1));
      for (let i = start; i <= end; i += 1) {
        const token = tokens[i];
        if (!token?.el) continue;
        const tokenRects = token.el.getClientRects();
        for (let r = 0; r < tokenRects.length; r += 1) {
          const rect = tokenRects[r];
          if (!rect || rect.width <= 0 || rect.height <= 0) continue;
          rects.push({
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height
          });
        }
      }
    });

    const previewRects = [];
    if (previewRange) {
      const start = Math.max(0, Math.min(previewRange.start, tokens.length - 1));
      const end = Math.max(0, Math.min(previewRange.end, tokens.length - 1));
      for (let i = start; i <= end; i += 1) {
        const token = tokens[i];
        if (!token?.el) continue;
        const tokenRects = token.el.getClientRects();
        for (let r = 0; r < tokenRects.length; r += 1) {
          const rect = tokenRects[r];
          if (!rect || rect.width <= 0 || rect.height <= 0) continue;
          previewRects.push({
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height
          });
        }
      }
    }

    let handles = null;
    if (previewRange) {
      const startToken = tokens[previewRange.start];
      const endToken = tokens[previewRange.end];
      const startRect = startToken?.el?.getClientRects()?.[0];
      const endRects = endToken?.el?.getClientRects();
      const endRect = endRects?.[endRects.length - 1];
      if (startRect && endRect) {
        handles = {
          start: { x: startRect.left - 8, y: startRect.top - 10 },
          end: { x: endRect.right - 8, y: endRect.bottom - 8 }
        };
      }
    }

    return { rects, previewRects, handles };
  }, [highlights, layoutTick, previewRange, tokenVersion]);

  if (!canPersist || pathname === '/login') {
    return null;
  }

  return (
    <>
      <Box className="igm-highlight-overlay" data-highlighter-control="true">
        {overlayData.rects.map((rect, idx) => (
          <Box
            key={`hl-${idx}`}
            className="igm-highlight-rect"
            style={{
              left: `${rect.left}px`,
              top: `${rect.top}px`,
              width: `${rect.width}px`,
              height: `${rect.height}px`
            }}
          />
        ))}
        {overlayData.previewRects.map((rect, idx) => (
          <Box
            key={`hl-preview-${idx}`}
            className="igm-highlight-rect igm-highlight-rect-preview"
            style={{
              left: `${rect.left}px`,
              top: `${rect.top}px`,
              width: `${rect.width}px`,
              height: `${rect.height}px`
            }}
          />
        ))}
      </Box>

      {markerEnabled && overlayData.handles ? (
        <>
          <Box
            className="igm-highlight-handle"
            style={{ left: `${overlayData.handles.start.x}px`, top: `${overlayData.handles.start.y}px` }}
            onTouchStart={handleHandleStart('start')}
            onMouseDown={handleHandleStart('start')}
            data-highlighter-control="true"
          />
          <Box
            className="igm-highlight-handle"
            style={{ left: `${overlayData.handles.end.x}px`, top: `${overlayData.handles.end.y}px` }}
            onTouchStart={handleHandleStart('end')}
            onMouseDown={handleHandleStart('end')}
            data-highlighter-control="true"
          />
        </>
      ) : null}

      <Box position="fixed" bottom={4} left={4} zIndex={1400} data-highlighter-control="true">
        {isPanelOpen ? (
          <VStack
            spacing={2}
            align="stretch"
            mb={2}
            p={2}
            borderRadius="md"
            bg="rgba(0, 0, 0, 0.78)"
            boxShadow="lg"
            minW="208px"
            data-highlighter-control="true"
          >
            <HStack spacing={2} data-highlighter-control="true">
              <Button
                size="xs"
                colorScheme={markerEnabled ? 'yellow' : 'teal'}
                leftIcon={<FaHighlighter />}
                onClick={() => setMarkerEnabled((previous) => !previous)}
                data-highlighter-control="true"
              >
                {markerEnabled ? 'Marcar texto ON' : 'Marcar texto OFF'}
              </Button>
              <Button
                size="xs"
                colorScheme="orange"
                variant="outline"
                leftIcon={<FaUndo />}
                onClick={handleUndoLastHighlight}
                isDisabled={highlights.length === 0}
                data-highlighter-control="true"
              >
                Desfazer
              </Button>
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                leftIcon={<FaTrash />}
                onClick={handleClearHighlights}
                isDisabled={highlights.length === 0}
                data-highlighter-control="true"
              >
                Limpar
              </Button>
            </HStack>
          </VStack>
        ) : null}

        <IconButton
          size="md"
          borderRadius="full"
          icon={<FaHighlighter />}
          aria-label="Ferramentas de marca texto"
          onClick={() => setIsPanelOpen((previous) => !previous)}
          bg={markerEnabled ? 'yellow.400' : 'rgba(15, 23, 42, 0.88)'}
          color={markerEnabled ? '#1A202C' : 'white'}
          border="2px solid rgba(255,255,255,0.92)"
          boxShadow="0 12px 26px rgba(2, 6, 23, 0.45)"
          _hover={{
            bg: markerEnabled ? 'yellow.300' : 'rgba(15, 23, 42, 0.96)',
            transform: 'scale(1.04)'
          }}
          _active={{
            bg: markerEnabled ? 'yellow.500' : 'rgba(15, 23, 42, 1)',
            transform: 'scale(0.98)'
          }}
          data-highlighter-control="true"
        />
      </Box>
    </>
  );
};

export default GlobalTextHighlighter;
